import { useCallback, useEffect, useRef, useState } from "react";

type NotificationType = "waiting" | "alert" | "success";

// Frequencies for different notification types
const NOTIFICATION_FREQUENCIES: Record<NotificationType, number[]> = {
  waiting: [523.25, 659.25, 783.99], // C5, E5, G5 - pleasant chime
  alert: [880, 1046.5], // A5, C6 - attention-grabbing
  success: [392, 523.25, 659.25], // G4, C5, E5 - success sound
};

export function useNotificationSound() {
  const audioContextRef = useRef<AudioContext | null>(null);
  const [isEnabled, setIsEnabled] = useState(() => {
    const stored = localStorage.getItem("notification-sound-enabled");
    return stored !== "false";
  });

  // Initialize AudioContext on first user interaction
  const initAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  const playSound = useCallback((type: NotificationType = "waiting") => {
    if (!isEnabled) return;

    try {
      const audioContext = initAudioContext();
      if (audioContext.state === "suspended") {
        audioContext.resume();
      }

      const frequencies = NOTIFICATION_FREQUENCIES[type];
      const duration = 0.15;
      const startTime = audioContext.currentTime;

      frequencies.forEach((freq, index) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(freq, startTime + index * duration);

        // Envelope for smooth sound
        gainNode.gain.setValueAtTime(0, startTime + index * duration);
        gainNode.gain.linearRampToValueAtTime(0.3, startTime + index * duration + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + index * duration + duration);

        oscillator.start(startTime + index * duration);
        oscillator.stop(startTime + index * duration + duration);
      });
    } catch (error) {
      console.error("Error playing notification sound:", error);
    }
  }, [isEnabled, initAudioContext]);

  const toggleSound = useCallback(() => {
    setIsEnabled((prev) => {
      const newValue = !prev;
      localStorage.setItem("notification-sound-enabled", String(newValue));
      return newValue;
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  return { playSound, isEnabled, toggleSound, initAudioContext };
}
